import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Scroll,
  Plus,
  MagnifyingGlass,
  Clock,
  DotsThree,
  Eye,
  PencilSimple,
  Trash,
  Play,
  UploadSimple
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import ImportModal from '../../components/ImportModal';

const ProtocolsPage = () => {
  const navigate = useNavigate();
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    loadProtocols();
  }, []);

  const loadProtocols = async () => {
    try {
      const data = await api.getProtocols();
      setProtocols(data);
    } catch (error) {
      console.error('Failed to load protocols:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this protocol?')) {
      try {
        await api.deleteProtocol(id);
        setProtocols(protocols.filter(p => p.id !== id));
        toast.success('Protocol deleted');
      } catch (error) {
        toast.error('Failed to delete protocol');
      }
    }
  };

  const filteredProtocols = protocols.filter(p =>
    p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="protocols-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display">Protocols</h1>
          <p className="text-zinc-500 mt-1">Standard operating procedures and protocols</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="btn-secondary flex items-center gap-2" onClick={() => setShowImport(true)} data-testid="import-protocol-btn">
            <UploadSimple size={16} />
            Import
          </Button>
          <Link to="/protocols/new">
            <Button className="btn-primary flex items-center gap-2" data-testid="new-protocol-btn">
              <Plus size={16} />
              New Protocol
            </Button>
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="card-flat p-4 mb-6">
        <div className="relative max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            type="text"
            placeholder="Search protocols..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 input-field w-full"
            data-testid="search-protocols"
          />
        </div>
      </div>

      {/* Protocols Grid */}
      {filteredProtocols.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProtocols.map((protocol) => (
            <div key={protocol.id} className="card-flat p-4 hover:border-zinc-300" data-testid={`protocol-card-${protocol.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <Link to={`/protocols/${protocol.id}`} className="heading-card text-slate-100 hover:text-indigo-300 block truncate">
                    {protocol.title}
                  </Link>
                  <p className="text-sm text-zinc-500 mt-1 line-clamp-2">{protocol.description || 'No description'}</p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1 hover:bg-zinc-100 rounded-sm">
                      <DotsThree size={20} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate(`/protocols/${protocol.id}`)}>
                      <Eye size={16} className="mr-2" /> View
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/protocols/${protocol.id}/bench`)}>
                      <Play size={16} className="mr-2" /> Bench Mode
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/protocols/${protocol.id}/edit`)}>
                      <PencilSimple size={16} className="mr-2" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDelete(protocol.id)} className="text-red-400">
                      <Trash size={16} className="mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex items-center gap-4 mt-4 text-sm text-zinc-500">
                <span className="flex items-center gap-1">
                  <Scroll size={14} />
                  {protocol.steps?.length || 0} steps
                </span>
                {protocol.duration_minutes && (
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {protocol.duration_minutes} min
                  </span>
                )}
              </div>
              {protocol.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {protocol.tags.slice(0, 3).map((tag, idx) => (
                    <span key={idx} className="text-xs bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded-sm">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state card-flat">
          <div className="empty-state-image" />
          <h3 className="heading-card text-slate-100 mb-2">No protocols yet</h3>
          <p className="text-sm text-zinc-500 mb-4">Create your first protocol to get started</p>
          <Link to="/protocols/new">
            <Button className="btn-primary">Create Protocol</Button>
          </Link>
        </div>
      )}

      {showImport && (
        <ImportModal type="protocols" onClose={() => setShowImport(false)} onImportComplete={() => loadProtocols()} />
      )}
    </div>
  );
};

export default ProtocolsPage;
